// Channel-point redemptions for the hosted overlay. Chat only carries a redemption
// when the reward asks for text; every other one reaches viewers through Twitch's
// channel-wide points feed on PubSub. That topic needs no login (the broadcaster-only
// and whisper topics answer ERR_BADAUTH to the same anonymous LISTEN), so the
// overlay can listen straight from the browser like it does for chat.

const PUBSUB_WS = 'wss://pubsub-edge.twitch.tv/v1';
// PubSub drops a socket that goes five minutes without a PING. Send one every four
// and reconnect if the PONG for the previous one never came back.
const PING_MS = 4 * 60_000;

export interface Redemption {
  id: string;
  userId: string;
  userLogin: string;
  userName: string;
  rewardTitle: string;
  rewardCost: number;
  rewardImage?: string;
  /** The reward's colour, which Twitch paints behind its art. */
  rewardBackground?: string;
  redeemedAt?: string;
}

interface RewardImage {
  url_1x?: string;
  url_2x?: string;
  url_4x?: string;
}

/** Listen for one channel's redemptions. Returns a disposer that stops
 *  reconnecting and closes the socket. Only rewards WITHOUT text reach the
 *  callback, since the ones with text already arrive as chat messages. */
export function connectChannelPoints(roomId: string, onRedemption: (r: Redemption) => void): () => void {
  let ws: WebSocket | null = null;
  let ping: ReturnType<typeof setInterval> | null = null;
  let disposed = false;
  let retry = 1000;

  const stopPing = () => {
    if (ping) clearInterval(ping);
    ping = null;
  };

  const connect = () => {
    if (disposed) return;
    const sock = new WebSocket(PUBSUB_WS);
    ws = sock;
    let awaitingPong = false;
    sock.onopen = () => {
      retry = 1000;
      sock.send(
        JSON.stringify({
          type: 'LISTEN',
          nonce: `sn-${Math.random().toString(36).slice(2)}`,
          data: { topics: [`community-points-channel-v1.${roomId}`] },
        }),
      );
      stopPing();
      ping = setInterval(() => {
        if (awaitingPong) {
          sock.close();
          return;
        }
        awaitingPong = true;
        sock.send(JSON.stringify({ type: 'PING' }));
      }, PING_MS);
    };
    sock.onmessage = (ev) => {
      let frame: { type?: string; error?: string; data?: { message?: string } };
      try {
        frame = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
      } catch {
        return;
      }
      if (frame.type === 'PONG') {
        awaitingPong = false;
      } else if (frame.type === 'RECONNECT') {
        sock.close();
      } else if (frame.type === 'RESPONSE' && frame.error) {
        // A refused LISTEN will be refused again; retrying would only hammer PubSub.
        disposed = true;
        sock.close();
      } else if (frame.type === 'MESSAGE' && frame.data?.message) {
        const r = parseRedemption(frame.data.message);
        if (r) onRedemption(r);
      }
    };
    sock.onclose = () => {
      stopPing();
      if (disposed) return;
      retry = Math.min(retry * 2, 60_000);
      setTimeout(connect, retry);
    };
    sock.onerror = () => sock.close();
  };

  connect();
  return () => {
    disposed = true;
    stopPing();
    ws?.close();
  };
}

function parseRedemption(raw: string): Redemption | null {
  let msg: {
    type?: string;
    data?: {
      redemption?: {
        id?: string;
        redeemed_at?: string;
        user?: { id?: string; login?: string; display_name?: string };
        reward?: {
          title?: string;
          cost?: number;
          is_user_input_required?: boolean;
          background_color?: string;
          image?: RewardImage | null;
          default_image?: RewardImage | null;
        };
      };
    };
  };
  try {
    msg = JSON.parse(raw);
  } catch {
    return null;
  }
  if (msg.type !== 'reward-redeemed') return null;
  const r = msg.data?.redemption;
  const reward = r?.reward;
  const login = r?.user?.login;
  if (!r?.id || !reward?.title || !login || reward.is_user_input_required) return null;
  const img = reward.image || reward.default_image;
  return {
    id: r.id,
    userId: r.user?.id || '',
    userLogin: login,
    userName: r.user?.display_name || login,
    rewardTitle: reward.title,
    rewardCost: typeof reward.cost === 'number' ? reward.cost : 0,
    rewardImage: img?.url_4x || img?.url_2x || img?.url_1x || undefined,
    // A hex colour or nothing: it lands in a CSS background, so nothing else passes.
    rewardBackground: /^#[0-9a-f]{3,8}$/i.test(reward.background_color ?? '') ? reward.background_color : undefined,
    redeemedAt: r.redeemed_at,
  };
}
